import { Link } from 'react-router-dom';
import FadeIn from '@/components/FadeIn';
import { useConsultation } from '@/components/ConsultationModal';
import { IMAGES } from '@/lib/constants';
import {
  Globe, ArrowLeft, ArrowUpRight, Check, Palette, Clock,
  Server, MessageSquare, Zap, Layers
} from 'lucide-react';

const portfolio = [
  {
    name: 'True Love Missionary Baptist Church',
    url: 'https://truelovemissionarybaptistchurch.com',
    category: 'Church & Community',
  },
  {
    name: 'Divine Care Youth',
    url: 'https://divine-care-youth.emergent.host/',
    category: 'Non-Profit',
  },
  {
    name: 'The Original M&M Soul Food',
    url: 'https://theoriginalmandmsoulfood.com',
    category: 'Restaurant',
  },
  {
    name: 'Wine Consigners',
    url: 'https://wineconsigners.com',
    category: 'Wine & Beverage',
  },
];

const services = [
  {
    icon: Zap,
    title: 'Same-Day Website',
    price: 'Starting at $250',
    description: 'Professional, conversion-focused website built and delivered the same day. Get your business online fast with a polished, mobile-responsive site.',
    details: [
      'Professional design and development',
      'Mobile-responsive layout',
      'Same-day delivery',
      'Includes 3 free hours of website maintenance',
    ],
  },
  {
    icon: Clock,
    title: 'Website Maintenance',
    price: '$60/hr',
    description: 'Need updates, edits, or improvements to your existing website? We handle all changes and updates at a straightforward hourly rate.',
    details: [
      'Content updates and edits',
      'Design adjustments',
      'Feature additions',
      'Performance optimization',
    ],
  },
  {
    icon: Palette,
    title: 'Logo Design',
    price: '$60 per design',
    description: 'Professional logo design to establish your brand identity. Each design package includes 5 logo variations to choose from.',
    details: [
      '5 logo variations per design',
      'Multiple format deliverables',
      'Brand-aligned concepts',
      'Revision included',
    ],
  },
  {
    icon: Server,
    title: 'Website Hosting',
    price: '$60/mo (first month free)',
    description: 'Reliable, managed website hosting with ongoing support. Your site stays fast, secure, and online around the clock.',
    details: [
      'Managed hosting infrastructure',
      'First month free',
      'Uptime monitoring',
      'Technical support included',
    ],
  },
  {
    icon: MessageSquare,
    title: 'Website Consultation',
    price: 'Starting at $100/hr',
    description: 'Expert consultation on your existing website. We assess your current site and provide actionable recommendations based on the complexity of your project.',
    details: [
      'Comprehensive site audit',
      'Performance and UX review',
      'Strategic improvement roadmap',
      'Pricing based on project complexity',
    ],
  },
];

export default function WebDesignPage() {
  const { setOpen } = useConsultation();

  return (
    <div data-testid="web-design-page">
      {/* HERO */}
      <section className="relative py-24 lg:py-32 bg-wc-charcoal overflow-hidden" data-testid="webdesign-hero">
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <FadeIn>
            <Link
              to="/associate-network"
              className="inline-flex items-center gap-2 font-body text-sm text-white/50 hover:text-white/80 transition-colors duration-300 mb-8"
              data-testid="webdesign-back-link"
            >
              <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Associate Network
            </Link>
            <div className="flex items-center gap-3 mb-4">
              <Globe className="w-6 h-6 text-wc-gold" strokeWidth={1.5} />
              <p className="font-accent text-xs uppercase tracking-[0.2em] text-wc-gold">Web Design</p>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-white max-w-4xl leading-[1.1]">
              Professional Websites Built for Your Business
            </h1>
            <p className="font-body text-base lg:text-lg text-white/55 mt-6 max-w-2xl leading-relaxed">
              Same-day website delivery, logo design, hosting, and ongoing maintenance. Everything your business needs to establish a strong digital presence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mt-10">
              <button
                onClick={() => setOpen(true)}
                className="bg-wc-gold text-white px-8 py-4 font-body text-xs uppercase tracking-[0.15em] hover:bg-wc-gold-hover transition-colors duration-300"
                data-testid="webdesign-hero-cta"
              >
                Get Started — From $250
              </button>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* PORTFOLIO */}
      <section className="py-24 lg:py-32 bg-white" data-testid="webdesign-portfolio">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <FadeIn>
            <div className="mb-16">
              <p className="font-accent text-xs uppercase tracking-[0.2em] text-wc-gold mb-4">Our Work</p>
              <h2 className="font-heading text-4xl sm:text-5xl font-light tracking-tight text-wc-black">
                Portfolio
              </h2>
            </div>
          </FadeIn>
          <div className="grid sm:grid-cols-2 gap-px bg-wc-border">
            {portfolio.map((site, i) => (
              <FadeIn key={i} delay={i * 100}>
                <a
                  href={site.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block bg-white p-10 lg:p-12 group hover:bg-wc-bg-alt transition-colors duration-300 h-full"
                  data-testid={`portfolio-item-${i}`}
                >
                  <p className="font-accent text-[10px] uppercase tracking-[0.2em] text-wc-gold mb-3">{site.category}</p>
                  <h3 className="font-heading text-xl sm:text-2xl font-normal text-wc-black mb-4 group-hover:text-wc-gold transition-colors duration-300">
                    {site.name}
                  </h3>
                  <div className="flex items-center gap-2 font-body text-sm text-wc-muted group-hover:text-wc-gold transition-colors duration-300">
                    <span className="truncate">{site.url.replace('https://', '')}</span>
                    <ArrowUpRight className="w-4 h-4 flex-shrink-0 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.5} />
                  </div>
                </a>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES & PRICING */}
      <section className="py-24 lg:py-32 bg-wc-bg-alt" data-testid="webdesign-services">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <FadeIn>
            <div className="text-center mb-16">
              <p className="font-accent text-xs uppercase tracking-[0.2em] text-wc-gold mb-4">Services & Pricing</p>
              <h2 className="font-heading text-4xl sm:text-5xl font-light tracking-tight text-wc-black">
                What We Offer
              </h2>
            </div>
          </FadeIn>
          <div className="space-y-px bg-wc-border">
            {services.map((service, i) => (
              <FadeIn key={i}>
                <div className="bg-white p-10 lg:p-12 grid lg:grid-cols-3 gap-8 items-start">
                  <div className="lg:col-span-1">
                    <div className="flex items-center gap-3 mb-3">
                      <service.icon className="w-5 h-5 text-wc-gold" strokeWidth={1.5} />
                    </div>
                    <h3 className="font-heading text-xl sm:text-2xl font-normal text-wc-black mb-2">{service.title}</h3>
                    <p className="font-body text-lg font-semibold text-wc-gold">{service.price}</p>
                  </div>
                  <div className="lg:col-span-2">
                    <p className="font-body text-sm text-wc-muted leading-relaxed mb-5">{service.description}</p>
                    <ul className="space-y-2">
                      {service.details.map((detail, j) => (
                        <li key={j} className="flex items-start gap-3">
                          <Check className="w-4 h-4 text-wc-gold flex-shrink-0 mt-0.5" strokeWidth={2} />
                          <span className="font-body text-sm text-wc-black">{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 lg:py-32" data-testid="webdesign-cta">
        <div className="absolute inset-0">
          <img src={IMAGES.darkTexture} alt="" className="w-full h-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-wc-black/85" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <FadeIn>
            <p className="font-accent text-xs uppercase tracking-[0.2em] text-wc-gold mb-6">Ready to Build?</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-light tracking-tight text-white mb-6">
              Get Your Website Today
            </h2>
            <p className="font-body text-base lg:text-lg text-white/55 max-w-xl mx-auto mb-10 leading-relaxed">
              Same-day professional websites starting at $250. Schedule a consultation and let's build something great for your business.
            </p>
            <button
              onClick={() => setOpen(true)}
              className="bg-wc-gold text-white px-10 py-4 font-body text-sm uppercase tracking-[0.15em] hover:bg-wc-gold-hover transition-colors duration-300"
              data-testid="webdesign-cta-button"
            >
              Schedule a Consultation
            </button>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
